/**
 * Sound Hook
 * Manages game sound effects with user preferences
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { ENV } from '../config/app.config';

const SOUND_ENABLED_KEY = 'pokercubed_sound_enabled';

// Sound effect types
export type SoundEffect =
  | 'card-deal'
  | 'action-fold'
  | 'action-call'
  | 'action-raise'
  | 'pot-win'
  | 'error'
  | 'notification';

// Simple sound database (we'll use data URIs for small beeps to avoid external files)
// In production, these would be replaced with actual sound files
const SOUND_URLS: Record<SoundEffect, string> = {
  'card-deal': 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwPUKXi77hkHgU4kdb0y3ktBSN2yO/ekUELFFyw6OynVRUKRp/f8L5sIAUsgs/y2Ik2Bxppt/DlnE4MD1Cl4u+4Yx4FOJDW9Mx5LQQjdsjv3pJBCxRcsOjsp1UUCkaf3/C+bCAFLIHP8tmJNgcaabfw5pxODA9QpeLvuGIeBjiP1vTMeS0EI3bI792SQQsUXLDo7KdVFApGnt/wvmwgBSyCz/LYiTYHGmm38OacTgwPUKXi77hjHgY4j9b0zHktBCN2yO/ekkELFFyw6OynVRQKRp7f8L5sIQUsgs/y2Ik2Bxppt/DlnE4MD1Cl4u+4Yx4GOI/W9Mx5LQUjdsju3pJBDBRbr+jrp1QUCkae3/C+bSEGK4LP8tiINgcaabfw5ZxODA9QpeL', // Placeholder
  'action-fold': 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACAhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwPUKXi77hkHgU4kdb0y3ktBSN2yO/ekUELFFyw6OynVRUKRp/f8L5sIAUsgs/y2Ik2Bxppt/DlnE4MD1Cl4u+4Yx4FOJDW9Mx5LQQjdsjv3pJBCxRcsOjsp1UUCkaf3/C+bCAFLIHP8tmJNgcaabfw5pxODA9QpeLvuGIeBjiP1vTMeS0EI3bI792SQQsUXLDo7KdVFApGnt/wvmwgBSyCz/LYiTYHGmm38OacTgwPUKXi77hjHgY4j9b0zHktBCN2yO/ekkELFFyw6OynVRQKRp7f8L5sIQUsgs/y2Ik2Bxppt/DlnE4MD1Cl4u+4Yx4GOI/W9Mx5LQUjdsju3pJBDBRbr+jrp1QUCkae3/C+bSEGK4LP8tiINgcaabfw5ZxODA9QpeL', // Placeholder
  'action-call': 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACAhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwPUKXi77hkHgU4kdb0y3ktBSN2yO/ekUELFFyw6OynVRUKRp/f8L5sIAUsgs/y2Ik2Bxppt/DlnE4MD1Cl4u+4Yx4FOJDW9Mx5LQQjdsjv3pJBCxRcsOjsp1UUCkaf3/C+bCAFLIHP8tmJNgcaabfw5pxODA9QpeLvuGIeBjiP1vTMeS0EI3bI792SQQsUXLDo7KdVFApGnt/wvmwgBSyCz/LYiTYHGmm38OacTgwPUKXi77hjHgY4j9b0zHktBCN2yO/ekkELFFyw6OynVRQKRp7f8L5sIQUsgs/y2Ik2Bxppt/DlnE4MD1Cl4u+4Yx4GOI/W9Mx5LQUjdsju3pJBDBRbr+jrp1QUCkae3/C+bSEGK4LP8tiINgcaabfw5ZxODA9QpeL', // Placeholder
  'action-raise': 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACAhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwPUKXi77hkHgU4kdb0y3ktBSN2yO/ekUELFFyw6OynVRUKRp/f8L5sIAUsgs/y2Ik2Bxppt/DlnE4MD1Cl4u+4Yx4FOJDW9Mx5LQQjdsjv3pJBCxRcsOjsp1UUCkaf3/C+bCAFLIHP8tmJNgcaabfw5pxODA9QpeLvuGIeBjiP1vTMeS0EI3bI792SQQsUXLDo7KdVFApGnt/wvmwgBSyCz/LYiTYHGmm38OacTgwPUKXi77hjHgY4j9b0zHktBCN2yO/ekkELFFyw6OynVRQKRp7f8L5sIQUsgs/y2Ik2Bxppt/DlnE4MD1Cl4u+4Yx4GOI/W9Mx5LQUjdsju3pJBDBRbr+jrp1QUCkae3/C+bSEGK4LP8tiINgcaabfw5ZxODA9QpeL', // Placeholder
  'pot-win': 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACAhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwPUKXi77hkHgU4kdb0y3ktBSN2yO/ekUELFFyw6OynVRUKRp/f8L5sIAUsgs/y2Ik2Bxppt/DlnE4MD1Cl4u+4Yx4FOJDW9Mx5LQQjdsjv3pJBCxRcsOjsp1UUCkaf3/C+bCAFLIHP8tmJNgcaabfw5pxODA9QpeLvuGIeBjiP1vTMeS0EI3bI792SQQsUXLDo7KdVFApGnt/wvmwgBSyCz/LYiTYHGmm38OacTgwPUKXi77hjHgY4j9b0zHktBCN2yO/ekkELFFyw6OynVRQKRp7f8L5sIQUsgs/y2Ik2Bxppt/DlnE4MD1Cl4u+4Yx4GOI/W9Mx5LQUjdsju3pJBDBRbr+jrp1QUCkae3/C+bSEGK4LP8tiINgcaabfw5ZxODA9QpeL', // Placeholder
  'error': 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACAhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwPUKXi77hkHgU4kdb0y3ktBSN2yO/ekUELFFyw6OynVRUKRp/f8L5sIAUsgs/y2Ik2Bxppt/DlnE4MD1Cl4u+4Yx4FOJDW9Mx5LQQjdsjv3pJBCxRcsOjsp1UUCkaf3/C+bCAFLIHP8tmJNgcaabfw5pxODA9QpeLvuGIeBjiP1vTMeS0EI3bI792SQQsUXLDo7KdVFApGnt/wvmwgBSyCz/LYiTYHGmm38OacTgwPUKXi77hjHgY4j9b0zHktBCN2yO/ekkELFFyw6OynVRQKRp7f8L5sIQUsgs/y2Ik2Bxppt/DlnE4MD1Cl4u+4Yx4GOI/W9Mx5LQUjdsju3pJBDBRbr+jrp1QUCkae3/C+bSEGK4LP8tiINgcaabfw5ZxODA9QpeL', // Placeholder
  'notification': 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACAhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwPUKXi77hkHgU4kdb0y3ktBSN2yO/ekUELFFyw6OynVRUKRp/f8L5sIAUsgs/y2Ik2Bxppt/DlnE4MD1Cl4u+4Yx4FOJDW9Mx5LQQjdsjv3pJBCxRcsOjsp1UUCkaf3/C+bCAFLIHP8tmJNgcaabfw5pxODA9QpeLvuGIeBjiP1vTMeS0EI3bI792SQQsUXLDo7KdVFApGnt/wvmwgBSyCz/LYiTYHGmm38OacTgwPUKXi77hjHgY4j9b0zHktBCN2yO/ekkELFFyw6OynVRQKRp7f8L5sIQUsgs/y2Ik2Bxppt/DlnE4MD1Cl4u+4Yx4GOI/W9Mx5LQUjdsju3pJBDBRbr+jrp1QUCkae3/C+bSEGK4LP8tiINgcaabfw5ZxODA9QpeL', // Placeholder
};

/**
 * Hook to manage sound effects with user preferences
 */
export const useSound = () => {
  const [isEnabled, setIsEnabled] = useState(() => {
    // Don't play sounds in test environment
    if (typeof window === 'undefined' || ENV === 'test') {
      return false;
    }

    const stored = localStorage.getItem(SOUND_ENABLED_KEY);
    // Default to enabled in dev/staging, disabled in production (user can enable)
    return stored !== null ? stored === 'true' : ENV !== 'production';
  });

  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBuffersRef = useRef<Map<SoundEffect, AudioBuffer>>(new Map());

  // Initialize audio context on first interaction (required by browsers)
  useEffect(() => {
    if (!isEnabled || typeof window === 'undefined') return;

    const initAudio = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
    };

    // Initialize on user interaction
    document.addEventListener('click', initAudio, { once: true });
    document.addEventListener('keydown', initAudio, { once: true });

    return () => {
      document.removeEventListener('click', initAudio);
      document.removeEventListener('keydown', initAudio);
    };
  }, [isEnabled]);

  /**
   * Toggle sound on/off and persist preference
   */
  const toggleSound = useCallback(() => {
    const newValue = !isEnabled;
    setIsEnabled(newValue);
    localStorage.setItem(SOUND_ENABLED_KEY, String(newValue));
  }, [isEnabled]);

  /**
   * Play a sound effect
   */
  const play = useCallback(async (effect: SoundEffect, volume: number = 0.3) => {
    // Don't play in test environment or if sounds disabled
    if (ENV === 'test' || !isEnabled || typeof window === 'undefined') {
      return;
    }

    try {
      const ctx = audioContextRef.current;
      if (!ctx) return;

      // For now, use simple beep tones instead of loading audio files
      // This avoids external dependencies and keeps it lightweight
      playBeep(ctx, effect, volume);
    } catch (error) {
      // Silently fail - sound is not critical
      console.debug('Failed to play sound:', effect, error);
    }
  }, [isEnabled]);

  return {
    isEnabled,
    toggleSound,
    play,
  };
};

/**
 * Play simple beep tone based on effect type
 * This is a lightweight alternative to loading audio files
 */
function playBeep(ctx: AudioContext, effect: SoundEffect, volume: number) {
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);

  // Different frequencies and durations for different effects
  const configs: Record<SoundEffect, { freq: number; duration: number; type: OscillatorType }> = {
    'card-deal': { freq: 440, duration: 0.05, type: 'sine' },
    'action-fold': { freq: 220, duration: 0.1, type: 'sine' },
    'action-call': { freq: 330, duration: 0.1, type: 'sine' },
    'action-raise': { freq: 550, duration: 0.15, type: 'sine' },
    'pot-win': { freq: 660, duration: 0.2, type: 'sine' },
    'error': { freq: 150, duration: 0.15, type: 'square' },
    'notification': { freq: 500, duration: 0.1, type: 'sine' },
  };

  const config = configs[effect];
  oscillator.type = config.type;
  oscillator.frequency.setValueAtTime(config.freq, ctx.currentTime);

  gainNode.gain.setValueAtTime(volume, ctx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + config.duration);

  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + config.duration);
}
